"use client";
// ButtonElement — NOYAU de rendu des boutons, SANS peinture (lot L, 04/10/2026). Extraction du moteur qui vivait dans
// `Button.tsx`, pas un second moteur : `@ubm/ui` Button (façade CVA) et `@ubm/product-ui` Button (via <PresentedButton>)
// le réemploient tous deux. Les classes, variantes, tons et la présentation appartiennent aux façades ; la peau Apple
// commune vit dans `native-fields-apple.css` (crochets `.ubm-ui-button` / `.ubm-btn`).
//
// Contrat (inchangé par rapport au moteur extrait) — un seul nœud interactif, jamais de bouton imbriqué :
//   · asChild → Slot : l'enfant unique reçoit className + props ; ni `type`, ni slots leading/trailing, ni target/rel/download ;
//   · href    → <a href> ; `aria-disabled` retire la navigation (href absent) et la tabulation (tabIndex -1) ; pas de `type` ;
//   · sinon   → <button>, `type` explicite ou "button" (un <button> sans type soumettrait son formulaire).
// leading / trailing : enveloppes décoratives `aria-hidden` rendues SEULEMENT si fournies — sans elles, les enfants sont
// rendus tels quels (aucun <span> ajouté). Ref, événements et props HTML arbitraires transmis au nœud rendu.
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";

export interface ButtonElementProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  /** Rend un `<a href>` (navigation) au lieu d'un `<button>`. */
  href?: string;
  target?: React.AnchorHTMLAttributes<HTMLAnchorElement>["target"];
  rel?: React.AnchorHTMLAttributes<HTMLAnchorElement>["rel"];
  download?: React.AnchorHTMLAttributes<HTMLAnchorElement>["download"];
  /** Icône menante (avant le label), décorative. */
  leading?: React.ReactNode;
  /** Icône suivante (après le label), décorative. */
  trailing?: React.ReactNode;
}

export const ButtonElement = React.forwardRef<HTMLButtonElement, ButtonElementProps>(
  ({ className, asChild = false, type, href, target, rel, download, leading, trailing, children, ...props }, ref) => {
    if (asChild) {
      return (
        <Slot ref={ref} className={className} {...props}>
          {children}
        </Slot>
      );
    }

    const body =
      leading == null && trailing == null ? (
        children
      ) : (
        <>
          {leading != null && (
            <span className="inline-grid shrink-0 place-items-center" aria-hidden>
              {leading}
            </span>
          )}
          {children}
          {trailing != null && (
            <span className="inline-grid shrink-0 place-items-center" aria-hidden>
              {trailing}
            </span>
          )}
        </>
      );

    if (href !== undefined) {
      const disabled = props.disabled || props["aria-disabled"] === true || props["aria-disabled"] === "true";
      return (
        <a
          ref={ref as unknown as React.Ref<HTMLAnchorElement>}
          href={disabled ? undefined : href}
          target={target}
          rel={rel}
          download={download}
          className={className}
          {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
          onClick={event => {
            if (disabled) { event.preventDefault(); event.stopPropagation(); return; }
            props.onClick?.(event as unknown as React.MouseEvent<HTMLButtonElement>);
          }}
          aria-disabled={disabled || undefined}
          tabIndex={disabled ? -1 : props.tabIndex}
        >
          {body}
        </a>
      );
    }

    return (
      <button ref={ref} type={type ?? "button"} className={className} {...props}>
        {body}
      </button>
    );
  },
);
ButtonElement.displayName = "ButtonElement";
